import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import {
  FaBookOpen,
  FaUsers,
  FaHistory,
  FaTrophy,
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaFileDownload,
  FaArrowRight
} from "react-icons/fa";
import "./Home.css";
import Loader from "./Loader.jsx";

const Home = () => {
  const [data, setData] = useState({
    slides: [],
    statistics: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch("/data/home.json");
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const jsonData = await response.json();
        setData(jsonData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 1000,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    fade: true,
    cssEase: "linear",
    arrows: false,
    pauseOnHover: false
  };

  const iconComponents = {
    FaBookOpen: <FaBookOpen />,
    FaUsers: <FaUsers />,
    FaHistory: <FaHistory />,
    FaTrophy: <FaTrophy />
  };

  // Table-of-contents style entries. Each list mirrors the sections
  // that used to be icon cards, now framed as an index of the forum's work.
  const eventEntries = [
    { to: "/barathivizha", label: "பாரதி விழா" },
    { to: "/asiriyarparatuvizha", label: "ஆசிரியர் பாராட்டு விழா" },
    { to: "/erode-bookfair", label: "ஈரோடு புத்தகத் திருவிழா" },
    { to: "/sirapunigalchigal", label: "சிறப்பு நிகழ்ச்சிகள்" },
    { to: "/piranigalchigal", label: "பங்கேற்கும் பிற நிகழ்ச்சிகள்" },
    { to: "/tamil-creations", label: "உலகத் தமிழர் படைப்பரங்கம்" },
    { to: "/vasagarvattam", label: "வார்டுதோறும் வாசகர் வட்டம்" }
  ];

  const workEntries = [
    { to: "/noolaga-pani", label: "நூலகப்பணி" },
    { to: "/podhu-pani", label: "பொதுப்பணி" },
    { to: "/varalatru-pani", label: "வரலாற்றுப்பணி" },
    { to: "/kalvi-pani", label: "கல்விப்பணி" },
    { to: "/meetings", label: "சந்திப்புகள்" },
    { to: "/specialmeetings", label: "சிறப்புச் சந்திப்புகள்" },
    { to: "/veliyeedugal", label: "வெளியீடுகள்" }
  ];

  if (loading) return <Loader />;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="home-container">
      {/* Hero */}
      <div className="hero">
        <Slider {...sliderSettings}>
          {data.slides.map((slide, index) => (
            <div key={index} className="hero-slide">
              <img
                src={slide.image}
                alt={`சமூக நிகழ்வு ${index + 1}`}
                loading="lazy"
              />
              <div className="hero-scrim" />
              <div className="hero-content">
                <span className="hero-eyebrow">மக்கள் சிந்தனைப் பேரவை</span>
                <h1 className="hero-title">{slide.title}</h1>
                <p className="hero-desc">{slide.description}</p>
                <Link to="/about" className="hero-cta">
                  மேலும் அறிய <FaArrowRight />
                </Link>
              </div>
            </div>
          ))}
        </Slider>
      </div>

      {/* Statistics */}
      <div className="stats-bar">
        <div className="stats-track">
          {data.statistics.map((stat, index) => (
            <div key={index} className="stat-item">
              <span className="stat-icon">{iconComponents[stat.icon]}</span>
              <span className="stat-value">{stat.value}</span>
              <span className="stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="page-content">
        {/* Table of contents style index of activities */}
        <section className="toc-section">
          <div className="toc-columns">
            <div className="toc-column">
              <div className="toc-header">
                <span className="toc-number">01</span>
                <h2>நிகழ்வுகள்</h2>
              </div>
              <ol className="toc-list">
                {eventEntries.map((item, i) => (
                  <li key={item.to} className="toc-entry">
                    <Link to={item.to} className="toc-link">
                      <span className="toc-index">{String(i + 1).padStart(2, "0")}</span>
                      <span className="toc-label">{item.label}</span>
                      <span className="toc-leader" />
                      <FaArrowRight className="toc-arrow" />
                    </Link>
                  </li>
                ))}
              </ol>
            </div>

            <div className="toc-column">
              <div className="toc-header">
                <span className="toc-number">02</span>
                <h2>அமைப்பின் பணிகள்</h2>
              </div>
              <ol className="toc-list">
                {workEntries.map((item, i) => (
                  <li key={item.to} className="toc-entry">
                    <Link to={item.to} className="toc-link">
                      <span className="toc-index">{String(i + 1).padStart(2, "0")}</span>
                      <span className="toc-label">{item.label}</span>
                      <span className="toc-leader" />
                      <FaArrowRight className="toc-arrow" />
                    </Link>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {/* Reader's circle callout */}
          <div className="reader-circle">
            <div className="reader-circle-text">
              <span className="toc-number">03</span>
              <div>
                <h3>வாசகர் வட்டம்</h3>
                <p>உறுப்பினர் படிவத்தைப் பதிவிறக்கம் செய்து இணையுங்கள்.</p>
              </div>
            </div>
            <a href="/puravalarform.pdf" className="reader-circle-btn" download>
              <FaFileDownload /> படிவம் பதிவிறக்கம்
            </a>
          </div>
        </section>

        {/* Patron programme */}
        <section className="patron-section">
          <header className="patron-header">
            <span className="patron-eyebrow">புரவலர் திட்டம்</span>
            <h2>மக்கள் சிந்தனைப் பேரவை புரவலர் திட்டம்</h2>
          </header>

          <div className="patron-intro">
            <p className="patron-lead">
              மக்கள் சிந்தனைப் பேரவையின் புரவலராக இணைய விரும்புவோரின் கனிவான
              கவனத்திற்கு...
            </p>
            <p className="patron-body">
              பேரவையின் சேவைத் திட்டங்களுக்கு ஆண்டுதோறும் ரூ 5,000
              நன்கொடையளிக்க முன்வரும் 1,000 நிரந்தர நன்கொடையாளர்கள் பேரவையின்
              புரவலர்களாக இணைக்கப்படவுள்ளனர்.
            </p>
          </div>

          <div className="patron-grid">
            <div className="patron-card">
              <h3>பங்களிப்பு வழிகள்</h3>
              <p className="patron-body">
                MAKKAL SINTHANAI PERAVAI PURAVALAR THITTAM பேங்க் ஆஃப்
                இந்தியா 'வில் தனிக் கணக்கு தொடங்கப்பட்டுள்ளது. அந்த வங்கிக்
                கணக்கிற்கு நேரடியாக ரூ 5,000 அனுப்பிவிட்டு அத்தகவலை பேரவையின்
                தலைமையகத்திற்கு மின்னஞ்சல், கூரியர், தபால் ஆகிய ஏதேனும்
                ஒன்றின் மூலம் தெரிவிக்க வேண்டுகிறோம்.
              </p>
              <dl className="bank-table">
                <div className="bank-row">
                  <dt>கணக்கு பெயர்</dt>
                  <dd>MAKKAL SINTHANAI PERAVAI PURAVALAR THITTAM</dd>
                </div>
                <div className="bank-row">
                  <dt>வங்கி பெயர்</dt>
                  <dd>STATE BANK OF INDIA</dd>
                </div>
                <div className="bank-row">
                  <dt>கணக்கு எண்</dt>
                  <dd>35952617590</dd>
                </div>
                <div className="bank-row">
                  <dt>கிளை</dt>
                  <dd>ERODE</dd>
                </div>
                <div className="bank-row">
                  <dt>IFSC குறியீடு</dt>
                  <dd>SBIN0051407</dd>
                </div>
              </dl>
            </div>

            <div className="patron-card">
              <h3>புரவலர் சலுகைகள்</h3>
              <ul className="benefits-list">
                <li>பேரவையின் அனைத்து நிகழ்ச்சிகளுக்கும் அழைப்பிதழ்</li>
                <li>முக்கிய நிகழ்ச்சிகளுக்கு முன்வரிசைகளில் அமரும் விதத்தில் VIP பாஸ்</li>
                <li>ஆண்டுக்கொருமுறை புரவலர்கள் கூட்டம்</li>
                <li>பேரவையின் வரவு-செலவு விபரங்கள் அறிக்கை</li>
                <li>வருமான வரி விலக்கு (80G)</li>
              </ul>
            </div>
          </div>

          <div className="patron-legal">
            <h3>சட்டபூர்வமான தகவல்கள்</h3>
            <p className="patron-body">
              பேரவை இந்திய அறக்கட்டளைச் சட்டத்தின்படி 08-09-2005 ஆம் தேதி
              அரசால் முறையாகப் பதிவுசெய்யப்பட்ட அமைப்பாகும். பதிவு எண்: 852 /
              2005. முறைப்படி ஆண்டுதோறும் வருமான வரித்துறைக்கு பேரவையின் வரவு
              செலவுக் கணக்கு சமர்ப்பிக்கப்பட்டு வருகிறது.
            </p>
            <p className="patron-body">
              பேரவைக்கு நன்கொடையளிக்கப்படும் தொகைக்கு இந்திய வருமானவரிச்
              சட்டப்பிரிவு 80 G யின் படி வருமான வரித்துறை
              வரிவிலக்களித்துள்ளது. இச்சலுகையை புரவலர்கள்
              பயன்படுத்திக்கொள்ளலாம்.
            </p>
          </div>

          <div className="patron-contact">
            <h3>தொடர்புக்கு</h3>
            <div className="contact-grid">
              <div className="contact-item">
                <FaPhoneAlt />
                <span>0424 - 2269186</span>
              </div>
              <div className="contact-item">
                <FaEnvelope />
                <span>info@makkalsinthanaiperavai.org</span>
              </div>
              <div className="contact-item">
                <FaMapMarkerAlt />
                <span>மாநிலத் தலைமை அலுவலகம், A - 47, சம்பத் நகர், ஈரோடு - 638 011</span>
              </div>
            </div>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="cta-band">
          <span className="cta-text">மக்கள் சிந்தனைப் பேரவை புரவலர் திட்டம்</span>
          <Link to="/contact" className="cta-btn">
            இணைய விரும்புகிறேன் <FaArrowRight />
          </Link>
        </section>
      </div>
    </div>
  );
};

export default Home;